/**
 * MARCA | Next Day Delivery Countdown
 * Displays a countdown timer to the cutoff time for next day delivery
 * Now shows countdown to next available day even after today's cutoff
 */

class NextDayDelivery {
  constructor(container) {
    this.container = container;
    this.messageContainer = container.querySelector('[data-message-container]');
    
    // Get settings from data attributes
    this.settings = {
      monday: {
        enabled: container.dataset.mondayEnabled === 'true',
        cutoff: container.dataset.mondayCutoff || '17:00'
      },
      tuesday: {
        enabled: container.dataset.tuesdayEnabled === 'true',
        cutoff: container.dataset.tuesdayCutoff || '17:00'
      },
      wednesday: {
        enabled: container.dataset.wednesdayEnabled === 'true',
        cutoff: container.dataset.wednesdayCutoff || '17:00'
      },
      thursday: {
        enabled: container.dataset.thursdayEnabled === 'true',
        cutoff: container.dataset.thursdayCutoff || '17:00'
      },
      friday: {
        enabled: container.dataset.fridayEnabled === 'true',
        cutoff: container.dataset.fridayCutoff || '17:00'
      },
      saturday: {
        enabled: container.dataset.saturdayEnabled === 'true',
        cutoff: container.dataset.saturdayCutoff || '17:00'
      },
      sunday: {
        enabled: container.dataset.sundayEnabled === 'true',
        cutoff: container.dataset.sundayCutoff || '17:00'
      }
    };

    this.messages = {
      nextDay: container.dataset.nextDayMessage || 'Order within {TIME} to get it tomorrow',
      futureDay: container.dataset.futureDayMessage || 'Want it {DAY}? Order within {TIME}',
      unavailable: container.dataset.unavailableMessage || 'Next day delivery not available'
    };

    this.dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    this.dayDisplayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    this.interval = null;

    this.init();
  }

  init() {
    this.updateCountdown();
    // Update every second
    this.interval = setInterval(() => this.updateCountdown(), 1000);
  }

  getDayName(dayIndex) {
    return this.dayNames[dayIndex];
  }

  getDayDisplayName(dayIndex) {
    return this.dayDisplayNames[dayIndex];
  }

  parseTime(timeString) {
    const [hours, minutes] = timeString.split(':').map(num => parseInt(num, 10));
    return { hours, minutes };
  }

  getCutoffDateTime(dayName, referenceDate = new Date()) {
    const { hours, minutes } = this.parseTime(this.settings[dayName].cutoff);
    const cutoffDate = new Date(referenceDate);
    cutoffDate.setHours(hours, minutes, 0, 0);
    return cutoffDate;
  }

  getNextBusinessDay(date) {
    // Returns the next business day (Monday-Friday) after the given date
    const nextDay = new Date(date);
    nextDay.setDate(date.getDate() + 1);
    
    // Keep adding days until we find a weekday
    while (nextDay.getDay() === 0 || nextDay.getDay() === 6) {
      nextDay.setDate(nextDay.getDate() + 1);
    }
    
    return nextDay;
  }

  getNextDeliveryInfo() {
    const now = new Date();
    
    // Look for the next enabled delivery day (check up to 14 days ahead to handle long weekends)
    for (let i = 0; i <= 14; i++) {
      const checkDate = new Date(now);
      checkDate.setDate(now.getDate() + i);
      
      const dayName = this.getDayName(checkDate.getDay());
      const daySettings = this.settings[dayName];

      if (daySettings.enabled) {
        const cutoffTime = this.getCutoffDateTime(dayName, checkDate);
        
        // For today (i === 0), check if cutoff has passed
        if (i === 0 && now >= cutoffTime) {
          continue; // Skip today if cutoff has passed
        }
        
        // Calculate when the order would ship
        // If ordering before cutoff, it ships today (checkDate)
        // Then delivery is next business day after ship date
        const shipDate = new Date(checkDate);
        const deliveryDate = this.getNextBusinessDay(shipDate);
        
        const daysUntilDelivery = Math.ceil((deliveryDate - now) / (1000 * 60 * 60 * 24));
        
        // Check if delivery is actually tomorrow (not just ordering before today's cutoff)
        const tomorrow = new Date(now);
        tomorrow.setDate(now.getDate() + 1);
        const isNextDay = deliveryDate.toDateString() === tomorrow.toDateString();
        
        return {
          cutoffTime: cutoffTime,
          deliveryDate: deliveryDate,
          deliveryDayName: this.getDayDisplayName(deliveryDate.getDay()),
          daysUntilDelivery: daysUntilDelivery,
          isNextDay: isNextDay,
          isAvailable: true
        };
      }
    }

    // No eligible days found in the next 2 weeks
    return {
      cutoffTime: null,
      deliveryDate: null,
      deliveryDayName: '',
      daysUntilDelivery: 0,
      isNextDay: false,
      isAvailable: false
    };
  }

  formatCountdown(milliseconds) {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    const parts = [];
    
    if (hours > 0) {
      parts.push(`${hours}h`);
    }
    if (minutes > 0 || hours > 0) {
      parts.push(`${minutes}m`);
    }
    parts.push(`${seconds}s`);

    return parts.join(' ');
  }

  parseMessageTemplate(template, countdown, dayName) {
    // Replace {TIME} with countdown
    let message = template.replace(/\{TIME\}/gi, `<strong class="next-day-delivery__time">${countdown}</strong>`);
    
    // Replace {DAY} with day name
    if (dayName) {
      message = message.replace(/\{DAY\}/gi, `<strong class="next-day-delivery__day">${dayName}</strong>`);
    }
    
    return message;
  }

  updateCountdown() {
    const deliveryInfo = this.getNextDeliveryInfo();

    if (!deliveryInfo.isAvailable) {
      // No delivery days enabled at all
      this.messageContainer.innerHTML = this.messages.unavailable;
      this.container.classList.add('next-day-delivery--unavailable');
      this.container.classList.remove('next-day-delivery--active');
      return;
    }

    // Calculate time remaining until cutoff
    const now = new Date();
    const timeRemaining = deliveryInfo.cutoffTime - now;

    if (timeRemaining > 0) {
      const countdown = this.formatCountdown(timeRemaining);
      
      // Choose the right message template
      let message;
      if (deliveryInfo.isNextDay) {
        // Ordering today for tomorrow
        message = this.parseMessageTemplate(this.messages.nextDay, countdown);
      } else {
        // Ordering for a future day (e.g., Tuesday evening for Thursday delivery)
        message = this.parseMessageTemplate(
          this.messages.futureDay, 
          countdown, 
          deliveryInfo.deliveryDayName
        );
      }
      
      this.messageContainer.innerHTML = message;
      this.container.classList.add('next-day-delivery--active');
      this.container.classList.remove('next-day-delivery--unavailable');
    } else {
      // Cutoff just passed, recalculate for next available day
      // This should trigger the loop to skip today and find tomorrow
      setTimeout(() => this.updateCountdown(), 100);
    }
  }

  destroy() {
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }
  }
}

// Initialize all next day delivery blocks on the page
document.addEventListener('DOMContentLoaded', () => {
  const containers = document.querySelectorAll('[data-next-day-delivery]');
  containers.forEach(container => {
    new NextDayDelivery(container);
  });
});

// Handle theme editor updates
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', () => {
    const containers = document.querySelectorAll('[data-next-day-delivery]');
    containers.forEach(container => {
      new NextDayDelivery(container);
    });
  });
}
